import React, { useState, useEffect, useCallback } from 'react';
import { getSupabase } from '@/lib/supabaseClient.js';
import Notifications from '@/components/Notifications';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from "@/components/ui/use-toast";
import { notificationService } from '@/services/notificationService';
import { useNavigate } from 'react-router-dom';

const NotificationHandler = () => {
  const supabase = getSupabase();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { toast } = useToast();
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    if (!user || !supabase) return;
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);
      if (error) throw error;
      setNotifications(data || []);
      setUnreadCount(data?.filter(n => !n.read_status).length || 0);
    } catch (error) {
      toast({ title: "Error", description: "Could not fetch notifications.", variant: "destructive" });
    }
  }, [user, supabase, toast]);

  useEffect(() => {
    console.log('NotificationHandler useEffect triggered', { user: !!user, supabase: !!supabase });
    if (!user || !supabase) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    // Only show last login notification if flagged by login event
    const showNotification = sessionStorage.getItem('showLastLoginNotification');
    console.log('Checking for last login notification flag:', showNotification);
    if (showNotification === 'true') {
      sessionStorage.removeItem('showLastLoginNotification');
      const lastLoginTimestamp = sessionStorage.getItem('lastLoginTimestamp');
      sessionStorage.removeItem('lastLoginTimestamp');
      console.log('Sending last login notification:', { userId: user.id, lastLoginTimestamp });
      (async () => {
        try {
          await notificationService.sendLastLoginNotification(user.id, lastLoginTimestamp || null);
          console.log('Last login notification sent successfully');
        } catch (error) {
          console.error('Error sending last login notification:', error);
        }
      })();
    }

    if (!supabase.realtime || typeof supabase.realtime.connect !== 'function') {
      return;
    }
    
    if (typeof window === 'undefined' || typeof window.WebSocket === 'undefined') {
      return;
    }

    fetchNotifications();
    let channel = null;
    
    try {
      channel = supabase
        .channel(`realtime-notifications-user-${user.id}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` }, payload => {
          console.log('Notification change received:', payload);
          fetchNotifications();
        })
        .subscribe((status, err) => {
          console.log('[NotificationHandler] Subscription status:', status, err);
          if (status === 'SUBSCRIBED') {
            console.log('[NotificationHandler] Successfully subscribed to notifications');
          }
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || err) {
            console.error('[NotificationHandler] Subscription error:', status, err);
            // Don't show toast for timeout errors, just log them
            if (status !== 'TIMED_OUT') {
              toast({
                title: "Real-time Notification Issue",
                description: "Could not connect for live notification updates. Error: " + (err?.message || status),
                variant: "destructive",
                duration: 5000,
              });
            }
          }
          if (status === 'CLOSED') {
            console.log('[NotificationHandler] Channel closed');
          }
        });
    } catch (error) {
      console.error('[NotificationHandler] Error setting up subscription:', error);
      toast({
          title: "Real-time Notification Issue",
          description: "Could not connect for live notification updates. Error: " + error.message,
          variant: "destructive",
          duration: 10000,
      });
      return;
    }
            
    return () => {
      if (channel && supabase.removeChannel) {
        supabase.removeChannel(channel).catch(removeError => 
          console.error("[NotificationHandler] Error removing notification channel:", removeError)
        );
      }
    };
  }, [user?.id, supabase, fetchNotifications]); // Include fetchNotifications in dependencies

  const handleNotificationClick = async (notification) => {
    if (!supabase) return;
    try {
      const { error } = await supabase.from('notifications').update({ read_status: true }).eq('id', notification.id);
      if (error) throw error;
      fetchNotifications();
      // Navigate based on notification type/content
      const type = notification?.type || '';
      const message = (notification?.message || '').toLowerCase();
      if (type === 'daily_spending_reminder' || message.includes('log your daily spending')) {
        navigate('/spending-tracker');
      }
    } catch (error) {
      toast({ title: "Error", description: "Could not update notification status.", variant: "destructive" });
    }
  };

  const handleClearAll = async () => {
    if (!user || !supabase) {
      return;
    }
    try {
      const { data, error } = await supabase
        .from('notifications')
        .update({ read_status: true })
        .eq('user_id', user.id)
        .eq('read_status', false) // Only update unread notifications
        .select('id');

      if (error) throw error;

      await fetchNotifications();

      toast({
        title: "Success",
        description: "All notifications marked as read.",
        variant: "default"
      });
    } catch (error) {
      console.error('Error clearing notifications:', error);
      toast({ title: "Error", description: "Could not clear notifications.", variant: "destructive" });
    }
  };

  const handleDeleteNotification = async (id) => {
    if (!supabase) return;
    try {
      const { error } = await supabase.from('notifications').delete().eq('id', id);
      if (error) throw error;
      fetchNotifications();
    } catch (error) {
      toast({ title: "Error", description: "Could not delete notification.", variant: "destructive" });
    }
  };

  if (!user) return null;

  return (
    <Notifications
      notifications={notifications}
      unreadCount={unreadCount}
      onNotificationClick={handleNotificationClick}
      onClearAll={handleClearAll}
      onDeleteNotification={handleDeleteNotification}
    />
  );
};

export default NotificationHandler;
  
